/**
 * ClientDetailPage — 接入应用详情
 * 展示应用完整信息；通过 / 驳回 / 删除操作放在此处（列表行不再直接提供）。
 */
import { useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
} from '@fluentui/react-components'
import { PlugConnectedRegular, CheckRegular, DismissRegular, DeleteRegular } from '@fluentui/react-icons'
import { clientsApi } from '@/api/clients'
import type { ClientApp } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { ROUTES } from '@/routes'
import DetailLayout, { DetailSection, DetailRow } from '@/components/DetailLayout'
import DangerButton from '@/components/DangerButton'
import { useDetailItem } from '@/hooks/useDetailItem'
import { formatDate } from '@/lib/format'

const STATUS: Record<string, { color: 'brand' | 'success' | 'danger' | 'subtle'; label: string }> = {
  pending: { color: 'brand', label: '待审核' },
  approved: { color: 'success', label: '已通过' },
  rejected: { color: 'danger', label: '已驳回' },
}

const useStyles = makeStyles({
  dialogActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    paddingTop: '8px',
  },
  deleteName: {
    fontWeight: 600,
    color: tokens.colorStatusDangerForeground1,
  },
})

export default function ClientDetailPage() {
  const styles = useStyles()
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { item, isLoading } = useDetailItem<ClientApp>(
    ['clients'],
    () => clientsApi.list(),
    id as string,
    'id',
  )

  const stateItem = (location.state as { item?: ClientApp } | null)?.item

  const [deleteOpen, setDeleteOpen] = useState(false)

  const reviewMutation = useMutation({
    mutationFn: (action: 'approve' | 'reject') => clientsApi.review(Number(id), action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('操作成功')
    },
    onError: (err) => {
      toast.error('操作失败', err instanceof ApiError ? err.message : '操作失败')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => clientsApi.delete(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('删除成功')
      navigate(ROUTES.CLIENTS)
    },
    onError: (err) => {
      toast.error('删除失败', err instanceof ApiError ? err.message : '删除失败')
    },
  })

  const current = item ?? stateItem

  return (
    <>
      <DetailLayout
        title={item?.name ?? stateItem?.name ?? '接入应用'}
        subtitle="网关接入应用"
        media={<PlugConnectedRegular fontSize={22} />}
        badge={
          <Badge appearance="filled" color={STATUS[(item ?? stateItem)?.status ?? '']?.color ?? 'subtle'}>
            {STATUS[(item ?? stateItem)?.status ?? '']?.label ?? (item ?? stateItem)?.status}
          </Badge>
        }
        loading={isLoading && !stateItem}
        notFound={!isLoading && !item && !stateItem}
        notFoundText="未找到该接入应用"
        actions={
          <>
            {(item ?? stateItem)?.status !== 'approved' && (
              <>
                <Button
                  appearance="subtle"
                  icon={<CheckRegular />}
                  disabled={reviewMutation.isPending}
                  onClick={() => reviewMutation.mutate('approve')}
                >
                  通过
                </Button>
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  disabled={reviewMutation.isPending}
                  onClick={() => reviewMutation.mutate('reject')}
                >
                  驳回
                </Button>
              </>
            )}
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              onClick={() => setDeleteOpen(true)}
            >
              删除
            </Button>
          </>
        }
      >
        <DetailSection title="应用信息">
          <DetailRow label="ID" value={item?.id ?? stateItem?.id} />
          <DetailRow label="名称" value={item?.name ?? stateItem?.name} />
          <DetailRow label="描述" value={item?.description ?? stateItem?.description} />
          <DetailRow label="API Key" value={item?.api_key ?? stateItem?.api_key} full />
          <DetailRow
            label="状态"
            value={STATUS[(item ?? stateItem)?.status ?? '']?.label ?? (item ?? stateItem)?.status}
          />
          <DetailRow
            label="限速"
            value={(item ?? stateItem)?.rate_limit === 0 ? '不限速' : `${(item ?? stateItem)?.rate_limit}/分`}
          />
          <DetailRow label="创建时间" value={formatDate((item ?? stateItem)?.created_at)} />
        </DetailSection>
      </DetailLayout>

      {/* 删除确认 */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(_, d) => {
          if (!d.open) setDeleteOpen(false)
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>确认删除</DialogTitle>
            <DialogContent>
              <Text>
                确定要删除接入应用{' '}
                <span className={styles.deleteName}>{current?.name}</span>{' '}
                吗？此操作不可撤销。
              </Text>
            </DialogContent>
            <div className={styles.dialogActions}>
              <Button onClick={() => setDeleteOpen(false)}>取消</Button>
              <DangerButton
                loading={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
              >
                确认删除
              </DangerButton>
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  )
}
